// plink.ts — the full-screen plink playfield.
//
// Tap or drag anywhere to play notes. Vertical position sets the pitch (high on
// screen = high note, snapped to a pentatonic scale). Dragging draws a luminous
// line of notes — like sketching a melody. Every note blooms as a glowing echo,
// and the board persists/fades each frame so motion leaves light trails.
//
// Peers' notes arrive via the relay and bloom in their own colour, turning a busy
// room into a shared canvas of overlapping light.
//
// Rendering is tuned for performance: no canvas shadowBlur (it's extremely slow),
// additive ("lighter") compositing for glow, pre-rendered glow sprites stamped
// with drawImage, and a translucent fade rect instead of per-ripple gradients.

import * as Tone from "tone";
import { hueForUserVoice, noteForY, triggerPlink, voiceForUser, type PlinkVoiceId } from "./audio";

export interface PlinkEvent {
  userId: string;
  x: number; // normalised 0..1
  y: number; // normalised 0..1
  note: string;
  voice?: PlinkVoiceId;
}

interface Ripple {
  x: number;
  y: number;
  hue: number;
  born: number; // performance.now() ms
  life: number;
  maxR: number;
}

interface Segment {
  x1: number; y1: number;
  x2: number; y2: number;
  hue: number;
  born: number;
}

interface Particle {
  x: number; y: number;
  vx: number; vy: number;
  r: number;
  hue: number;
}

export interface PlinkField {
  remote(ev: PlinkEvent): void;
  pulse(): void;
  setVoice(v: PlinkVoiceId): void;
  dispose(): void;
}

const FADE = "rgba(7, 7, 12, 0.20)"; // per-frame persistence overlay
const RIPPLE_LIFE = 900;
const SEG_LIFE = 550;
const PARTICLE_COUNT = 48;

// --- pre-rendered radial glow sprite, cached per coarse hue ------------------
const spriteCache = new Map<number, HTMLCanvasElement>();
function glowSprite(hue: number): HTMLCanvasElement {
  const key = Math.round(hue / 8) * 8;
  let s = spriteCache.get(key);
  if (s) return s;
  const size = 128;
  s = document.createElement("canvas");
  s.width = size;
  s.height = size;
  const c = s.getContext("2d");
  if (c) {
    const grad = c.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    grad.addColorStop(0, `hsla(${key}, 95%, 72%, 0.95)`);
    grad.addColorStop(0.35, `hsla(${key}, 90%, 60%, 0.4)`);
    grad.addColorStop(1, `hsla(${key}, 90%, 55%, 0)`);
    c.fillStyle = grad;
    c.fillRect(0, 0, size, size);
  }
  spriteCache.set(key, s);
  return s;
}

export function createPlinkField(
  root: HTMLElement,
  localUserId: string,
  onLocalPlink: (ev: { x: number; y: number; note: string }) => void,
): PlinkField {
  const canvas = document.createElement("canvas");
  canvas.className = "plink-canvas";
  root.appendChild(canvas);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2d canvas context unavailable");
  const g = ctx as CanvasRenderingContext2D;

  const dpr = Math.min(1.5, window.devicePixelRatio || 1);
  let w = 0;
  let h = 0;

  function resize(): void {
    w = root.clientWidth;
    h = root.clientHeight;
    canvas.width = Math.max(1, Math.floor(w * dpr));
    canvas.height = Math.max(1, Math.floor(h * dpr));
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
  }
  resize();
  const ro = new ResizeObserver(resize);
  ro.observe(root);

  const ripples: Ripple[] = [];
  const segments: Segment[] = [];
  const particles: Particle[] = [];
  let beatPulse = 0;

  let localVoice: PlinkVoiceId = voiceForUser(localUserId);
  let localHue = hueForUserVoice(localUserId, localVoice);

  // seed ambient drifting particles so the board is alive when idle
  for (let i = 0; i < PARTICLE_COUNT; i++) {
    particles.push({
      x: Math.random(),
      y: Math.random(),
      vx: (Math.random() - 0.5) * 0.00006,
      vy: (Math.random() - 0.5) * 0.00006,
      r: 0.6 + Math.random() * 1.6,
      hue: (localHue + (Math.random() - 0.5) * 50 + 360) % 360,
    });
  }

  function bloom(xPx: number, yPx: number, hue: number): void {
    // hard cap so a refocused/backgrounded tab flushing queued draws can't flood
    if (ripples.length > 240) ripples.splice(0, ripples.length - 240);
    ripples.push({ x: xPx, y: yPx, hue, born: performance.now(), life: RIPPLE_LIFE, maxR: Math.min(w, h) * 0.4 });
  }

  // Play a note at a pixel position. `time` lets the beat scheduler place it
  // exactly on a 16th-note tick; without it we quantise to the next 16th.
  function fire(px: number, py: number, time?: number): void {
    if (w <= 0 || h <= 0) return;
    const nx = Math.min(1, Math.max(0, px / w));
    const ny = Math.min(1, Math.max(0, py / h));
    const note = noteForY(ny);
    const transport = Tone.getTransport();
    let t = time;
    if (t === undefined) {
      try {
        t = transport.state === "started" ? Number(transport.nextSubdivision("16n")) : Tone.now();
      } catch {
        t = Tone.now();
      }
    }
    triggerPlink(localVoice, note, t);
    const at = t;
    Tone.getDraw().schedule(() => bloom(px, py, localHue), at);
    onLocalPlink({ x: nx, y: ny, note });
  }

  // --- input: tap = one note, hold/drag = a note on every 16th at the finger --
  let pointerDown = false;
  let lastX = 0;
  let lastY = 0;

  function localPoint(clientX: number, clientY: number): { px: number; py: number } | null {
    if (w <= 0 || h <= 0) return null;
    const rect = canvas.getBoundingClientRect();
    return { px: clientX - rect.left, py: clientY - rect.top };
  }

  function onDown(e: PointerEvent): void {
    const p = localPoint(e.clientX, e.clientY);
    if (!p) return;
    pointerDown = true;
    lastX = p.px;
    lastY = p.py;
    try { canvas.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    fire(p.px, p.py); // immediate tap note (quantised to the next 16th)
  }

  function onMove(e: PointerEvent): void {
    if (!pointerDown) return;
    const p = localPoint(e.clientX, e.clientY);
    if (!p) return;
    // draw the glowing connecting stroke; the beat scheduler emits the notes
    const dist = Math.hypot(p.px - lastX, p.py - lastY);
    if (dist > 2) {
      if (segments.length > 240) segments.splice(0, segments.length - 240);
      segments.push({ x1: lastX, y1: lastY, x2: p.px, y2: p.py, hue: localHue, born: performance.now() });
    }
    lastX = p.px;
    lastY = p.py;
  }

  // While the pointer is held, fire a note on every 8th-note tick at the finger's
  // current position — so a held/dragged gesture is locked to the groove. (8th
  // notes = ~2× fewer than 16ths; change DRAW_SUBDIV to "4n" for sparser.)
  const DRAW_SUBDIV = "8n";
  const drawTick = Tone.getTransport().scheduleRepeat((time) => {
    if (pointerDown) fire(lastX, lastY, time);
  }, DRAW_SUBDIV);

  function onUp(e: PointerEvent): void {
    pointerDown = false;
    try { canvas.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
  }

  canvas.addEventListener("pointerdown", onDown);
  canvas.addEventListener("pointermove", onMove);
  canvas.addEventListener("pointerup", onUp);
  canvas.addEventListener("pointercancel", onUp);

  // --- render loop -----------------------------------------------------------
  let raf = 0;
  let last = performance.now();

  function frame(): void {
    const now = performance.now();
    const dt = Math.min(64, now - last);
    last = now;

    // persistence fade — leaves glowing trails, cheap, no clearRect needed
    beatPulse *= 0.9;
    g.globalCompositeOperation = "source-over";
    g.fillStyle = FADE;
    g.fillRect(0, 0, w, h);

    // a faint downbeat wash
    if (beatPulse > 0.01) {
      g.fillStyle = `hsla(${localHue}, 80%, 55%, ${beatPulse * 0.05})`;
      g.fillRect(0, 0, w, h);
    }

    g.globalCompositeOperation = "lighter";

    // ambient particles
    for (const p of particles) {
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      if (p.x < 0) p.x += 1; else if (p.x > 1) p.x -= 1;
      if (p.y < 0) p.y += 1; else if (p.y > 1) p.y -= 1;
      g.fillStyle = `hsla(${p.hue}, 80%, 70%, 0.10)`;
      g.beginPath();
      g.arc(p.x * w, p.y * h, p.r, 0, Math.PI * 2);
      g.fill();
    }

    // drawn strokes
    for (let i = segments.length - 1; i >= 0; i--) {
      const s = segments[i];
      if (!s) continue;
      const p = (now - s.born) / SEG_LIFE;
      if (p >= 1) { segments.splice(i, 1); continue; }
      const alpha = (1 - p) * 0.6;
      g.strokeStyle = `hsla(${s.hue}, 95%, 68%, ${alpha})`;
      g.lineWidth = 3 * (1 - p) + 1;
      g.lineCap = "round";
      g.beginPath();
      g.moveTo(s.x1, s.y1);
      g.lineTo(s.x2, s.y2);
      g.stroke();
    }

    // ripples — expanding ring + stamped glow core
    for (let i = ripples.length - 1; i >= 0; i--) {
      const r = ripples[i];
      if (!r) continue;
      const p = (now - r.born) / r.life;
      if (p >= 1) { ripples.splice(i, 1); continue; }
      const ease = 1 - Math.pow(1 - p, 3);
      const radius = r.maxR * ease;
      const alpha = (1 - p) * (1 - p);

      g.strokeStyle = `hsla(${r.hue}, 92%, 66%, ${alpha * 0.8})`;
      g.lineWidth = 2.5 * (1 - p) + 0.5;
      g.beginPath();
      g.arc(r.x, r.y, radius, 0, Math.PI * 2);
      g.stroke();

      if (p < 0.5) {
        const flash = (1 - p / 0.5);
        const size = 70 * flash + 30;
        g.globalAlpha = flash;
        g.drawImage(glowSprite(r.hue), r.x - size / 2, r.y - size / 2, size, size);
        g.globalAlpha = 1;
      }
    }

    g.globalCompositeOperation = "source-over";
    raf = requestAnimationFrame(frame);
  }
  raf = requestAnimationFrame(frame);

  return {
    remote(ev: PlinkEvent): void {
      const voice = ev.voice ?? voiceForUser(ev.userId);
      const hue = hueForUserVoice(ev.userId, voice);
      const note = ev.note || noteForY(ev.y);
      triggerPlink(voice, note);
      bloom(ev.x * w, ev.y * h, hue);
    },
    pulse(): void {
      beatPulse = 1;
    },
    setVoice(v: PlinkVoiceId): void {
      localVoice = v;
      localHue = hueForUserVoice(localUserId, v);
    },
    dispose(): void {
      cancelAnimationFrame(raf);
      Tone.getTransport().clear(drawTick);
      ro.disconnect();
      canvas.removeEventListener("pointerdown", onDown);
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerup", onUp);
      canvas.removeEventListener("pointercancel", onUp);
      canvas.remove();
    },
  };
}
